import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "../../../layouts/MobileLayout";
import AttendanceHome from "../../../components/mobile/AttendanceHome";
import { useAuthStore } from "../../../store/authStore";
import { api } from "../../../utils/api";
import { useLoaderStore } from "../../../store/loaderStore";
import { toast } from "react-toastify";

const AttendancePage = () => {
  const [date, setDate] = useState(new Date());
  const [activeStartDate, setActiveStartDate] = useState(new Date());


  const navigate = useNavigate();

  const { user, isAuthenticated } = useAuthStore();
  const { showLoader, hideLoader } = useLoaderStore();

  const companyId = user?.company_id;
  const userId = user?.user_id;

  const [todayLog, setTodayLog] = useState({
    first_punch_id: 0,
    first_punch: "",
    last_punch_id: 0,
    last_punch: "",
  });

  const [shiftDetails, setShiftDetails] = useState({
    shift_name: "",
    shift_start_time: "",
    shift_end_time: "",
    type: "",
    message: "",
  });

  const [attendanceLog, setAttendanceLog] = useState([]);

  const presentDays = attendanceLog
    .filter((item) => item.type === "Present")
    .map((item) => new Date(item.date).getDate());

  const absentDays = attendanceLog
    .filter((item) => item.type === "Absent")
    .map((item) => new Date(item.date).getDate());

  const leaveDays = attendanceLog
    .filter((item) => item.type === "Leave")
    .map((item) => new Date(item.date).getDate());

  const holidayDays = attendanceLog
    .filter((item) => item.type === "Holiday")
    .map((item) => new Date(item.date).getDate());

  const weekOffDays = attendanceLog
    .filter((item) => item.type === "Week Off")
    .map((item) => new Date(item.date).getDate());

  const formatTime24 = (dateTime) => {
    if (!dateTime) return "-";

    return new Date(dateTime).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatShiftTime24 = (time) => {
    if (!time) return "-";

    const [hours, minutes] = time.split(":");

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}`;
  };

  const formatShiftTime = (time) => {
    if (!time) return "-";

    const [hours, minutes] = time.split(":");

    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    const fetchSelectedDateDetails = async () => {
      if (!companyId || !userId || !isAuthenticated) return;
      showLoader("master");
      const selectedDate = date.toLocaleDateString("en-CA");

      try {
        // =========================
        // FETCH ATTENDANCE
        // =========================
        // const attendanceResponse = await fetch(
        //   `${process.env.REACT_APP_API_BASE_URL}/attendance/attendance-log?company_id=${companyId}&user_id=${userId}&from_date=${selectedDate}&to_date=${selectedDate}`,
        //   {
        //     headers: {
        //       Authorization: `Bearer ${token}`,
        //       "Content-Type": "application/json",
        //     },
        //   },
        // );

        const attendanceResponse = await api.get(
          `/attendance/attendance-log?company_id=${companyId}&user_id=${userId}&from_date=${selectedDate}&to_date=${selectedDate}`,
        );

        const attendanceResult = await attendanceResponse.json();

        if (
          attendanceResponse.ok &&
          attendanceResult.status &&
          attendanceResult.data?.length > 0
        ) {
          const selectedDay = attendanceResult.data[0];

          setTodayLog({
            first_punch_id: selectedDay.first_punch_id || 0,
            first_punch: selectedDay.first_punch || "",
            last_punch_id: selectedDay.last_punch_id || 0,
            last_punch: selectedDay.last_punch || "",
          });
        } else {
          setTodayLog({
            first_punch_id: 0,
            first_punch: "",
            last_punch_id: 0,
            last_punch: "",
          });
        }

        // =========================
        // FETCH SHIFT
        // =========================
        const shiftResponse = await api.get(
          `/attendance/shift-for-date?company_id=${companyId}&user_id=${userId}&target_date=${selectedDate}`,
        );

        const shiftResult = await shiftResponse.json();
        if (shiftResponse.ok && shiftResult.status) {
          setShiftDetails({
            shift_name: shiftResult.shift_name || "",
            shift_start_time: shiftResult.shift_start_time || "",
            shift_end_time: shiftResult.shift_end_time || "",
            type: shiftResult.type || "",
            message: shiftResult.message || "",
          });
        } else {
          setShiftDetails({
            shift_name: "",
            shift_start_time: "",
            shift_end_time: "",
            type: "",
            message: "",
          });
        }
      } catch (error) {
        console.error("Error fetching selected date details:", error);

        setTodayLog({
          first_punch: "",
          last_punch: "",
        });

        setShiftDetails({
          shift_name: "",
          shift_start_time: "",
          shift_end_time: "",
        });
      } finally {
        hideLoader();
      }
    };

    fetchSelectedDateDetails();
  }, [date, companyId, userId, isAuthenticated]);

  useEffect(() => {
    const fetchAttendanceLog = async () => {
      try {
        const year = activeStartDate.getFullYear();
        const month = activeStartDate.getMonth() + 1;

        const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;

        const lastDay = new Date(year, month, 0).getDate();

        const toDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

        const response = await api.get(
          `/attendance/attendance-log?company_id=${companyId}&user_id=${userId}&from_date=${fromDate}&to_date=${toDate}`,
        );

        const result = await response.json();

        if (response.ok && result.status) {
          setAttendanceLog(result.data || []);
        } else {
          setAttendanceLog([]);
        }
      } catch (err) {
        console.error(err);
        setAttendanceLog([]);
      }
    };

    if (companyId && userId && isAuthenticated) {
      fetchAttendanceLog();
    }
  }, [companyId, userId, isAuthenticated, activeStartDate]);

  const tileClassName = ({ date: tileDate, view }) => {
    if (view !== "month") return null;

    const isCurrentMonth =
      tileDate.getMonth() === activeStartDate.getMonth() &&
      tileDate.getFullYear() === activeStartDate.getFullYear();

    if (!isCurrentMonth) {
      return "neighbor-month";
    }

    const day = tileDate.getDate();

    if (holidayDays.includes(day)) return "holiday-day";
    if (weekOffDays.includes(day)) return "weekoff-day";
    if (leaveDays.includes(day)) return "leave-day";
    if (presentDays.includes(day)) return "present-day";
    if (absentDays.includes(day)) return "absent-day";

    return null;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = new Date(date);
  selectedDate.setHours(0, 0, 0, 0);

  const isFutureDate = selectedDate > today;

  const isRegularizeDisabled =
    isFutureDate ||
    shiftDetails.type === "Weekoff" ||
    shiftDetails.type === "Holiday";

  const handleRegularize = () => {
    const now = new Date();
    const today = new Date();

    // Remove time portion for accurate date comparison
    today.setHours(0, 0, 0, 0);

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    // =========================
    // FUTURE DATE
    // =========================
    if (selectedDate > today) {
      toast.warning("You cannot regularize attendance for a future date.");
      return;
    }

    // =========================
    // WEEK OFF
    // =========================
    if (shiftDetails.type === "Weekoff") {
      toast.info(
        shiftDetails.message || "Regularization is not allowed on a Week Off.",
      );
      return;
    }

    // =========================
    // HOLIDAY
    // =========================
    if (shiftDetails.type === "Holiday") {
      toast.info(
        shiftDetails.message || "Regularization is not allowed on a Holiday.",
      );
      return;
    }

    // =========================
    // TODAY - CHECK SHIFT END TIME
    // =========================
    if (
      selectedDate.getTime() === today.getTime() &&
      shiftDetails.shift_end_time
    ) {
      const [endHour, endMinute] = shiftDetails.shift_end_time
        .split(":")
        .map(Number);

      const shiftEnd = new Date();
      shiftEnd.setHours(endHour, endMinute, 0, 0);

      // Current time is before shift end
      if (now < shiftEnd) {
        toast.warning(
          `You cannot regularize attendance before your shift ends at ${formatShiftTime(
            shiftDetails.shift_end_time,
          )}.`,
        );
        return;
      }
    }

    // =========================
    // NORMAL ATTENDANCE DATE
    // =========================
    navigate("/user/mobile/attendanceform", {
      state: {
        punchId: todayLog.first_punch_id || 0,
        outId: todayLog.last_punch_id || 0,

        date: date.toLocaleDateString("en-GB"),

        inTime: todayLog.first_punch
          ? formatTime24(todayLog.first_punch)
          : formatShiftTime24(shiftDetails.shift_start_time),

        outTime: todayLog.last_punch
          ? formatTime24(todayLog.last_punch)
          : formatShiftTime24(shiftDetails.shift_end_time),

        shiftName: shiftDetails.shift_name,
        shiftType: shiftDetails.type,

        shiftStartTime: shiftDetails.shift_start_time,
        shiftEndTime: shiftDetails.shift_end_time,
      },
    });
  };

  return (
    <MobileLayout>
      <AttendanceHome
        date={date}
        onDateChange={setDate}
        activeStartDate={activeStartDate}
        onActiveStartDateChange={setActiveStartDate}
        tileClassName={tileClassName}
        shiftDetails={shiftDetails}
        todayLog={todayLog}
        isRegularizeDisabled={isRegularizeDisabled}
        onRegularize={handleRegularize}
        onHistoryClick={() => navigate("/user/mobile/attendance-log")}
        onTeamClick={() => navigate("/user/mobile/team-attendance")}
      />
    </MobileLayout>
  );
};

export default AttendancePage;
